import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { collection, addDoc, serverTimestamp, doc, updateDoc, getDoc, query, where, getDocs, setDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { MigrationDataAccessLayer } from './migrationDataAccessLayer';
import Logger from './logger';
import { LOAD_STATUS, SHIPPING_SCHEDULE_STATUS, PO_STATUS } from '../constants/statusValues';
import { NOTIFICATION_TYPES, NOTIFICATION_STATUS, PARTNER_REQUEST_STATUS } from '../constants/notificationTypes';
import {
  RATE_CONFIRMATION_ACCEPTED_AT,
  RATE_CONFIRMATION_ACCEPTED_BY,
  RATE_CONFIRMATION_SIGNATURE_NAME,
  type RateConfirmationAcceptancePayload
} from '../constants/rateConfirmationConstants';
import {
  filterNotificationDocs,
  getNotificationDocsForPoAndParty,
} from './notificationFirestoreQueries';
import { scheduleLocationVisibilityRefresh } from './locationVisibilityService';

// Constants for dynamic behavior
const USER_TYPES = {
  SHIPPER: 'shipper',
  BROKER: 'broker'
} as const;

export interface LoadRequestNotification {
  id?: string;
  carrierId: string;
  shipperId?: string; // Optional for broker loads
  userId?: string; // For broker loads
  brokerId?: string; // For broker loads
  userType?: 'shipper' | 'broker'; // To distinguish between shipper and broker loads
  type?: string; // Notification type (e.g., make_offer, carrier_counter_offer)
  shippingScheduleId?: string; // Optional for broker loads
  loadId?: string;
  poNumber?: string; // For broker loads
  status: 'pending' | 'accepted' | 'rejected' | 'counter_offer'; // Using string literals for interface compatibility
  read?: boolean; // Whether the notification has been read
  requiresAction?: boolean; // Whether the notification requires action from the user
  loadDetails: {
    pickupLocation: {
      address: string;
      city: string;
      state: string;
      zipCode: string;
      date: string;
      time: string;
    };
    deliveryLocation: {
      address: string;
      city: string;
      state: string;
      zipCode: string;
      date: string;
      time: string;
    };
    dimensions: {
      length: number;
      width: number;
      height: number;
    };
    weight: number;
    rate: number;
    shipperCompany?: string;
    poNumber?: string;
    carrierOption?: string;
  };
  createdAt: any;
  updatedAt: any;
}

export const sendLoadRequestToCarrier = async (
  carrierId: string,
  shipperId: string,
  shippingScheduleId: string,
  loadDetails: LoadRequestNotification['loadDetails'],
  poNumber?: string
) => {
  // Fetch shipper profile for senderName using dual-mode access
  let senderName = '';
  try {
    const migrationDataAccess = new MigrationDataAccessLayer();
    const result = await migrationDataAccess.getUserProfile(shipperId);
    if (result.success && result.data) {
      const shipperProfile = result.data;
      senderName = shipperProfile.companyName || shipperProfile.displayName || '';
    }
  } catch (err) {
    Logger.error('[sendLoadRequestToCarrier] Error fetching shipper profile:', err as Error);
  }

  const notificationRef = collection(getFailoverAwareDatabase(), 'notifications');
  const notificationData: Omit<LoadRequestNotification, 'id'> & { recipientId: string; type: string; message: string; senderName: string; read: boolean; poNumber?: string } = {
    carrierId,
    shipperId,
    recipientId: carrierId, // Ensure carrier receives the notification
    shippingScheduleId,
    status: NOTIFICATION_STATUS.PENDING,
    loadDetails,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    type: NOTIFICATION_TYPES.MAKE_OFFER,
    message: 'You have a new load request.',
    senderName,
    read: false,
    ...(poNumber && { poNumber })
  };

  const docRef = await addDoc(notificationRef, notificationData);
  return docRef.id;
};

export const updateLoadRequestStatus = async (
  poNumber: string,
  status: LoadRequestNotification['status'],
  counterOffer?: number,
  carrierId?: string,
  notificationId?: string
) => {
  
  return new Promise(async (resolve, reject) => {
    // Get current user directly instead of using onAuthStateChanged
    const auth = getFailoverAwareAuth();
    const user = auth.currentUser;
    
    if (!user?.uid && !carrierId) {
      reject(new Error('User not authenticated'));
      return;
    }
    
    const userId = carrierId || user?.uid;
      
      try {
        let notificationDoc: any = null;
        let currentData: LoadRequestNotification | null = null;
        
        // PRODUCTION FIX: If notificationId is provided, read notification first, process business logic, then update
        // This ensures load is created BEFORE notification is marked as read
        if (notificationId) {
          const notificationRef = doc(getFailoverAwareDatabase(), 'notifications', notificationId);
          const notificationSnap = await getDoc(notificationRef);
          
          if (!notificationSnap.exists()) {
            reject(new Error(`Notification ${notificationId} not found`));
            return;
          }
          
          notificationDoc = notificationSnap;
          currentData = notificationSnap.data() as LoadRequestNotification;
          
          // Verify this notification belongs to the user and matches the PO
          const isOwner = currentData.userId === userId || 
                         currentData.shipperId === userId || 
                         currentData.brokerId === userId;
          const matchesPO = currentData.poNumber === poNumber;
          
          if (!isOwner || !matchesPO) {
            reject(new Error(`Notification ${notificationId} does not belong to user or match PO ${poNumber}`));
            return;
          }
          
          // CRITICAL: Process business logic FIRST (create/update load), THEN update notification
          // This ensures load exists before notification is marked as read
          // This matches the working broker flow pattern
          
        } else {
          const notificationDocs = filterNotificationDocs(
            await getNotificationDocsForPoAndParty(getFailoverAwareDatabase(), poNumber, userId!),
            {
              types: [
                NOTIFICATION_TYPES.PARTNER_REQUEST,
                NOTIFICATION_TYPES.MAKE_OFFER,
                NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
                NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
              ],
            }
          );

          if (notificationDocs.length === 0) {
            reject(new Error(`No notification found for PO ${poNumber}`));
            return;
          }

          const userScoped = notificationDocs.filter((docSnap) => {
            const data = docSnap.data();
            const isCarrierRole = data?.carrierId === userId || data?.recipientId === userId;
            const isOwnerRole = data?.userId === userId || data?.shipperId === userId || data?.brokerId === userId;
            return isCarrierRole || isOwnerRole;
          });

          if (userScoped.length === 0) {
            reject(new Error(`No relevant notification found for PO ${poNumber} and user ${userId}`));
            return;
          }

          const prioritizedTypes = [
            NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
            NOTIFICATION_TYPES.MAKE_OFFER,
            NOTIFICATION_TYPES.UNIFIED_PARTNER_REQUEST,
            NOTIFICATION_TYPES.PARTNER_REQUEST
          ];

          const getTimestampMs = (d: any) => {
            const u = d?.updatedAt;
            const c = d?.createdAt;
            const t = u || c;
            if (t?.toMillis) return t.toMillis();
            if (t instanceof Date) return t.getTime();
            return 0;
          };

          const sorted = [...userScoped].sort((a, b) => getTimestampMs(b.data()) - getTimestampMs(a.data()));
          notificationDoc = sorted.find(doc => {
            const d = doc.data();
            return !!d?.carrierId && prioritizedTypes.includes(d?.type);
          }) || sorted[0];
          
          if (!notificationDoc) {
            reject(new Error('No notification found to process'));
            return;
          }
          
          currentData = notificationDoc.data() as LoadRequestNotification;
          
          // CRITICAL: Don't update notification here - process business logic first
          // Update will happen after business logic succeeds (see below)
        }
        
        if (!currentData) {
          reject(new Error('Notification data is null or undefined'));
          return;
        }

        // CRITICAL: Process business logic FIRST (create/update load), THEN update notification
        // This ensures load is created successfully before notification is marked as read
        // This prevents notification from disappearing if load creation fails
        
        let businessLogicCompleted = false;
        
        try {
          // Handle business logic based on status using poNumber
          if (status === NOTIFICATION_STATUS.REJECTED) {
            // For shipper-carrier workflow, use simple rejection logic
            await handleLoadRejection(poNumber, notificationDoc.id);
            businessLogicCompleted = true;
          } else if (status === NOTIFICATION_STATUS.ACCEPTED) {
            // For shipper-carrier workflow, use simple acceptance logic
            await handleLoadAcceptance(poNumber, notificationDoc.id);
            businessLogicCompleted = true;
          } else if (status === NOTIFICATION_STATUS.COUNTER_OFFER && counterOffer !== undefined) {
            await handleCounterOffer(poNumber, notificationDoc.id, counterOffer);
            businessLogicCompleted = true;
          }
        } catch (businessError) {
          // If business logic fails, don't update notification - let it remain actionable
          Logger.error('[updateLoadRequestStatus] Business logic failed, notification not updated:', businessError as Error);
          reject(businessError);
          return;
        }
        
        // CRITICAL FIX: Update ALL notifications for this PO, not just one
        // This fixes the issue where multiple notifications exist and only one gets updated
        if (businessLogicCompleted) {
          const updateData: Partial<LoadRequestNotification> = {
            status,
            updatedAt: serverTimestamp()
          };

          if (status === NOTIFICATION_STATUS.ACCEPTED || status === NOTIFICATION_STATUS.REJECTED) {
            updateData.read = true;
            updateData.requiresAction = false;
          }

          if (counterOffer !== undefined) {
            updateData.loadDetails = {
              ...(currentData.loadDetails || {}),
              rate: counterOffer
            };
          }

          // Update the specific notification first
          const notificationRef = notificationId 
            ? doc(getFailoverAwareDatabase(), 'notifications', notificationId)
            : doc(getFailoverAwareDatabase(), 'notifications', notificationDoc.id);
          
          await updateDoc(notificationRef, updateData);
          Logger.debug('[updateLoadRequestStatus] Updated specific notification:', {
            notificationId: notificationId || notificationDoc.id,
            status,
            read: updateData.read,
            requiresAction: updateData.requiresAction
          });

          // PRODUCTION FIX: Update ALL other notifications for this PO with the same type that belong to this user
          // This ensures all related notifications are marked as read/requiresAction: false
          // Only update notifications that belong to the same user (shipperId, brokerId, or userId match)
          try {
            // Get the user ID from the notification (validated earlier)
            const userOwnershipField = currentData.shipperId || currentData.brokerId || currentData.userId;

            const allNotificationDocs = filterNotificationDocs(
              await getNotificationDocsForPoAndParty(getFailoverAwareDatabase(), poNumber, userId!),
              currentData.type ? { types: [currentData.type] } : {}
            );

            const userNotifications = allNotificationDocs.filter((docSnap) => {
              const data = docSnap.data();
              if (!userOwnershipField) return false;
              const isOwner = data.userId === userOwnershipField ||
                             data.shipperId === userOwnershipField ||
                             data.brokerId === userOwnershipField;
              return isOwner;
            });
            
            if (userNotifications.length > 1) {
              Logger.debug(`[updateLoadRequestStatus] Found ${userNotifications.length} user-owned notifications for PO ${poNumber}, updating all of them`);
              
              const updatePromises = userNotifications
                .filter(doc => doc.id !== (notificationId || notificationDoc.id)) // Skip the one we already updated
                .map(doc => updateDoc(doc.ref, updateData));
              
              if (updatePromises.length > 0) {
                await Promise.all(updatePromises);
                Logger.debug(`[updateLoadRequestStatus] Updated ${updatePromises.length} additional notifications for PO ${poNumber}`);
              }
            }
          } catch (bulkUpdateError) {
            Logger.error('[updateLoadRequestStatus] Error updating all notifications for PO:', bulkUpdateError as Error);
            // Don't throw - we already updated the main notification
          }
        }
        
        resolve(undefined);
      } catch (error) {
        reject(error);
      }
    });
};

// Helper function to check if a carrier is partnered with a shipper
export const checkCarrierPartnership = async (shipperId: string, carrierId: string): Promise<boolean> => {
  try {
    const migrationDataAccess = new MigrationDataAccessLayer();
    const result = await migrationDataAccess.getPartners(shipperId);
    if (result.success && result.data) {
      return result.data.some(partner => partner.id === carrierId);
    }
    return false;
  } catch (error) {
    Logger.error('[checkCarrierPartnership] Error checking partnership:', error as Error);
    return false;
  }
};

// Helper function to check if a carrier is partnered with a broker
export const checkBrokerPartnership = async (brokerId: string, carrierId: string): Promise<boolean> => {
  try {
    const migrationDataAccess = new MigrationDataAccessLayer();
    const result = await migrationDataAccess.getPartners(brokerId);
    if (result.success && result.data) {
      return result.data.some(partner => partner.id === carrierId);
    }
    return false;
  } catch (error) {
    Logger.error('[checkBrokerPartnership] Error checking partnership:', error as Error);
    return false;
  }
};

export const acceptLoadForPO = async (
  poNumber: string,
  carrierId: string,
  isPartnered: boolean,
  counterOfferAmount?: number,
  rateConfirmationAcceptance?: RateConfirmationAcceptancePayload
) => {
  Logger.debug('🚨 acceptLoadForPO CALLED with:', { poNumber, carrierId, isPartnered, counterOfferAmount, rateConfirmationAcceptance: !!rateConfirmationAcceptance });

  // Validate inputs
  if (!poNumber || !carrierId) {
    Logger.error('[acceptLoadForPO] Missing required parameters:', new Error('Missing required parameters'), { poNumber, carrierId });
    throw new Error('Missing required parameters');
  }

  // FIX 1: Auto-fetch counter offer amount if not provided (like acceptLoadForBrokerPO)
  if (!counterOfferAmount) {
    try {
      const counterOfferDocs = filterNotificationDocs(
        await getNotificationDocsForPoAndParty(getFailoverAwareDatabase(), poNumber, carrierId),
        { types: [NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER], status: NOTIFICATION_STATUS.COUNTER_OFFER }
      );
      if (counterOfferDocs.length > 0) {
        const counterOfferData = counterOfferDocs[0].data();
        counterOfferAmount = counterOfferData.loadDetails?.rate;
        Logger.debug('[acceptLoadForPO] Auto-fetched counter offer amount:', { counterOfferAmount });
      }
    } catch (error) {
      Logger.warn('[acceptLoadForPO] Error checking for counter offers:', error as Error);
      // Continue without counter offer if lookup fails
    }
  }

  // 1. Find PO by poNumber
  const poSnapshot = await getDocs(query(collection(getFailoverAwareDatabase(), 'purchaseOrders'), where('poNumber', '==', poNumber)));
  if (poSnapshot.empty) {
    Logger.error('[acceptLoadForPO] PO not found for poNumber:', new Error('PO not found'), { poNumber });
    throw new Error('PO not found');
  }
  const poDoc = poSnapshot.docs[0];
  let poId = poDoc.id; // FIX: Changed to 'let' to allow reassignment for multi-load child PO
  const poData = poDoc.data();

  // Validate that carrierId is not the same as the shipper's userId
  if (poData.userId === carrierId) {
    Logger.error('[acceptLoadForPO] carrierId cannot be the same as shipper userId:', new Error('Invalid carrierId'), { carrierId, shipperUserId: poData.userId });
    throw new Error('Invalid carrier assignment: carrierId matches shipper userId');
  }

  // 2. Check if carrier is actually partnered with shipper (override parameter)
  const isActuallyPartnered = await checkCarrierPartnership(poData.userId, carrierId);
  Logger.debug('[acceptLoadForPO] Partnership check result:', { isActuallyPartnered });
  Logger.debug('[acceptLoadForPO] Function called with parameters:', { poNumber, carrierId, isPartnered, counterOfferAmount });

  // FIX 2: Marketplace loads should be 'Active' after shipper accepts (same as partnered)
  // When shipper accepts counter offer, the load is approved and should be Active
  let newStatus: string;
  let newShippingScheduleStatus: string;
  
  // Both partnered and marketplace go to Active after acceptance
  newStatus = LOAD_STATUS.ACTIVE;
  newShippingScheduleStatus = SHIPPING_SCHEDULE_STATUS.ACTIVE;
  Logger.debug('[acceptLoadForPO] Setting statuses to Active (marketplace and partnered both go to Active after acceptance)');

  // 3. Fetch carrier profile using dual-mode access
  let carrierProfile: Record<string, any> = {};
  try {
    const migrationDataAccess = new MigrationDataAccessLayer();
    const result = await migrationDataAccess.getUserProfile(carrierId);
    if (result.success && result.data) {
      carrierProfile = result.data;
    }
  } catch (err) {
    Logger.error('[acceptLoadForPO] Error fetching carrier profile:', err as Error);
  }

  // 4. Prepare carrier info
  const carrierInfo = {
    id: carrierId,
    companyName: carrierProfile.companyName || '',
    email: carrierProfile.email || '',
    acceptedAt: new Date().toISOString(),
    status: isActuallyPartnered ? 'approved' : 'pending'
  };

  // 5. Write to carriers subcollection (optional - not critical for workflow)
  try {
    await setDoc(doc(getFailoverAwareDatabase(), 'purchaseOrders', poId, 'carriers', carrierId), carrierInfo);
    Logger.debug('[acceptLoadForPO] Successfully wrote to carriers subcollection');
  } catch (err) {
    Logger.warn('[acceptLoadForPO] Could not write to carriers subcollection (permission restricted):', err as Error);
    // This is expected for non-partner carriers due to Firestore rules
    // The carrier review workflow will still function correctly
  }

  // 6. Update PO summary fields - FIX: Both marketplace and partnered go to Active after acceptance
  // CRITICAL: Update ALL PO documents with this poNumber to handle duplicates
  try {
    const poUpdateData: any = {
      carrierId: carrierId, // Add carrierId for permission validation
      shippingScheduleStatus: newShippingScheduleStatus,
      status: newStatus,
      carrierOption: 'carrier',
      selectedCarrier: carrierInfo,
      // FIX: After shipper accepts, both marketplace and partnered use approvedCarrier
      approvedCarrier: carrierInfo
    };

    Logger.debug('[acceptLoadForPO] Updating PO with data:', {
      poNumber,
      carrierId,
      isActuallyPartnered,
      newStatus,
      newShippingScheduleStatus,
      counterOfferAmount
    });

    // Update pricing if counter offer was made so Shipper Purchase Orders reflects new rate
    if (counterOfferAmount && counterOfferAmount > 0) {
      poUpdateData.amount = counterOfferAmount;
      poUpdateData.rate = counterOfferAmount;
      poUpdateData.total = counterOfferAmount;
    }

    // CRITICAL: Update ALL PO documents with this poNumber (handle duplicates)
    const allPOsQuery = query(
      collection(getFailoverAwareDatabase(), 'purchaseOrders'),
      where('poNumber', '==', poNumber)
    );
    const allPOSnap = await getDocs(allPOsQuery);
    
    if (allPOSnap.empty) {
      throw new Error(`No PO found with poNumber ${poNumber}`);
    }
    
    // Rate confirmation acceptance: only set if not already set (idempotency)
    const acceptanceFields = rateConfirmationAcceptance
      ? {
          [RATE_CONFIRMATION_ACCEPTED_AT]: rateConfirmationAcceptance.acceptedAt,
          [RATE_CONFIRMATION_ACCEPTED_BY]: rateConfirmationAcceptance.acceptedBy,
          ...(rateConfirmationAcceptance.signatureName != null && rateConfirmationAcceptance.signatureName !== ''
            ? { [RATE_CONFIRMATION_SIGNATURE_NAME]: rateConfirmationAcceptance.signatureName }
            : {})
        }
      : {};
    
    // Update all PO documents with this poNumber
    const updatePromises = allPOSnap.docs.map(poDocRef => {
      const existingPoData = poDocRef.data();
      const includeAcceptance =
        rateConfirmationAcceptance && !existingPoData[RATE_CONFIRMATION_ACCEPTED_AT];
      return updateDoc(doc(getFailoverAwareDatabase(), 'purchaseOrders', poDocRef.id), {
        ...poUpdateData,
        ...(includeAcceptance ? acceptanceFields : {}),
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
    Logger.debug('[acceptLoadForPO] Successfully updated', { count: allPOSnap.size, poNumber });
    Logger.debug('[acceptLoadForPO] Successfully updated PO in database');
    
    // CRITICAL: Verify ALL PO documents have correct status
    try {
      const verifyPOQuery = query(
        collection(getFailoverAwareDatabase(), 'purchaseOrders'),
        where('poNumber', '==', poNumber)
      );
      const verifyPOSnap = await getDocs(verifyPOQuery);
      
      if (verifyPOSnap.empty) {
        Logger.error('[acceptLoadForPO] CRITICAL: No PO documents found for verification!', new Error('No PO documents found'), { poNumber });
      } else {
        const expectedStatus = newStatus.toLowerCase();
        const fixPromises: Promise<any>[] = [];
        
        verifyPOSnap.docs.forEach(poDoc => {
          const verifyPOData = poDoc.data();
          const actualStatus = (verifyPOData.status || '').toLowerCase();
          
          if (actualStatus !== expectedStatus) {
            Logger.error('[acceptLoadForPO] CRITICAL: PO status mismatch after update!', new Error('PO status mismatch'), {
              poNumber,
              poId: poDoc.id,
              expected: newStatus,
              actual: verifyPOData.status
            });
            // Auto-fix: Force update to correct status
            fixPromises.push(
              updateDoc(doc(getFailoverAwareDatabase(), 'purchaseOrders', poDoc.id), {
                status: newStatus,
                shippingScheduleStatus: newShippingScheduleStatus,
                updatedAt: serverTimestamp()
              })
            );
          }
        });
        
        if (fixPromises.length > 0) {
          await Promise.all(fixPromises);
          Logger.debug('[acceptLoadForPO] Auto-fixed', { count: fixPromises.length, status: newStatus });
        } else {
          Logger.debug('[acceptLoadForPO] ✅ All', { count: verifyPOSnap.size, status: newStatus });
        }
      }
    } catch (verifyError) {
      Logger.error('[acceptLoadForPO] Error verifying PO status:', verifyError as Error);
      // Don't throw - verification is safety check, not critical
    }

  } catch (err) {
    Logger.error('[acceptLoadForPO] CRITICAL ERROR updating PO:', err as Error);
    // CRITICAL: PO update failure means load won't show on carrier dashboard
    // Throw error to prevent continuing with inconsistent state
    throw new Error(`Failed to update PO status to Active: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }

  // 6. Handle multi-load sequential allocation if this is a parent PO
  let assignedChildPONumber: string | null = null;
  let remainingLoads: number | undefined = undefined;
  let isMultiLoadParent = false;
  let originalParentPONumber: string | null = null;
  
  // Check if this is a multi-load parent PO (marketplace load)
  if (poData.isMultiLoad && !poData.parentPONumber) {
    isMultiLoadParent = true;
    originalParentPONumber = poData.poNumber; // Store original parent PO number
    try {
      const { multiLoadService } = await import('./multiLoadService');
      const result = await multiLoadService.assignCarrierToMultiLoad(
        poData.poNumber,
        carrierId,
        'purchaseOrders'
      );
      
      if (result.success && result.childPONumber) {
        assignedChildPONumber = result.childPONumber;
        remainingLoads = result.remainingLoads;
        Logger.debug('[acceptLoadForPO] Multi-load sequential allocation successful:', {
          childPONumber: assignedChildPONumber,
          remainingLoads,
          originalParentPONumber
        });
        
        // Update the PO number to use the assigned child PO for rest of workflow
        poNumber = assignedChildPONumber;
        
        // Re-fetch the child PO data
        const childPOSnapshot = await getDocs(query(
          collection(getFailoverAwareDatabase(), 'purchaseOrders'),
          where('poNumber', '==', assignedChildPONumber)
        ));
        if (!childPOSnapshot.empty) {
          const childPODoc = childPOSnapshot.docs[0];
          const childPOData = childPODoc.data();
          // Update poData to use child PO data
          Object.assign(poData, childPOData);
          poId = childPODoc.id;
        }
      }
    } catch (multiLoadError) {
      Logger.warn('[acceptLoadForPO] Error in multi-load sequential allocation:', multiLoadError as Error);
      // Continue with parent PO if allocation fails
    }
  }

  // 7. Update the corresponding load in 'loads' collection to set carrierId
  // FIX: Ensure load is always created/updated for partner requests and marketplace loads
  let loadCreatedOrUpdated = false;
  let createdLoadId: string | null = null;
  
  try {
    if (poData.poNumber) {
      // Determine if this is a multi-load child PO (after potential parent allocation)
      // This check happens AFTER multi-load parent allocation (if any) so we use the correct PO data
      const isChildPO = poData.isMultiLoad === true && poData.parentPONumber;
      
      // Fetch multi-load fields if this is a child PO (used for both update and create paths)
      let multiLoadFields: any = {};
      if (isChildPO) {
        // This is a multi-load child PO - fetch parent PO to get totalLoads
        try {
          const { multiLoadService } = await import('./multiLoadService');
          const parentPO = await multiLoadService.getParentPOByChild(poData.poNumber, 'purchaseOrders');
          if (parentPO) {
            multiLoadFields = {
              isMultiLoad: true,
              parentPONumber: poData.parentPONumber,
              loadSequence: poData.loadSequence || 0,
              totalLoads: parentPO.totalLoads || 0
            };
            Logger.debug('[acceptLoadForPO] Multi-load child PO detected:', multiLoadFields);
          }
        } catch (multiLoadError) {
          Logger.warn('[acceptLoadForPO] Error fetching parent PO for multi-load:', multiLoadError as Error);
          // Continue without multi-load fields if fetch fails (backward compatibility)
        }
      }
      
      // SEPARATED QUERY LOGIC: Multi-load and single load use different approaches
      let existingLoadDoc = null;
      
      if (isChildPO) {
        // ============================================
        // MULTI-LOAD CHILD PO PATH
        // ============================================
        // Multi-load child POs require composite query with carrierId to find the correct child load
        // and exclude parent marketplace loads
        try {
          const partnerLoadQuery = query(
            collection(getFailoverAwareDatabase(), 'loads'),
            where('poNumber', '==', poData.poNumber),
            where('carrierId', '==', carrierId)
          );
          const partnerLoadSnap = await getDocs(partnerLoadQuery);
          if (!partnerLoadSnap.empty) {
            // Find load that's not a parent marketplace load (child loads have parentPONumber)
            const partnerLoad = partnerLoadSnap.docs.find(doc => {
              const data = doc.data();
              return !(data.isMultiLoad === true && !data.parentPONumber);
            });
            if (partnerLoad) {
              existingLoadDoc = partnerLoad;
              Logger.debug('[acceptLoadForPO] Found multi-load child partner load:', { loadId: partnerLoad.id });
            }
          }
        } catch (partnerQueryError) {
          Logger.warn('[acceptLoadForPO] Error querying for multi-load child load:', partnerQueryError as Error);
          // Fallback: Query by poNumber only for multi-load
          try {
            const loadsQuery = query(
              collection(getFailoverAwareDatabase(), 'loads'),
              where('poNumber', '==', poData.poNumber)
            );
            const loadsSnap = await getDocs(loadsQuery);
            // Filter for child loads (have parentPONumber) with matching carrierId
            existingLoadDoc = loadsSnap.docs.find(doc => {
              const data = doc.data();
              // Must be a child load (has parentPONumber) and match carrierId
              return data.carrierId === carrierId && 
                     !(data.isMultiLoad === true && !data.parentPONumber);
            });
          } catch (fallbackError) {
            Logger.warn('[acceptLoadForPO] Error in multi-load fallback query:', fallbackError as Error);
          }
        }
      } else {
        // ============================================
        // SINGLE LOAD PATH (production-ready with retry)
        // ============================================
        // PRODUCTION FIX: Try composite query first (if index exists), then fallback to poNumber-only query
        // Retry mechanism for transient failures
        let queryAttempts = 0;
        const maxQueryAttempts = 3;
        let querySucceeded = false;
        
        while (queryAttempts < maxQueryAttempts && !querySucceeded) {
          try {
            queryAttempts++;
            
            // Try composite query first (requires index: poNumber + carrierId)
            try {
              const compositeQuery = query(
                collection(getFailoverAwareDatabase(), 'loads'),
                where('poNumber', '==', poData.poNumber),
                where('carrierId', '==', carrierId)
              );
              const compositeSnap = await getDocs(compositeQuery);
              
              if (!compositeSnap.empty) {
                existingLoadDoc = compositeSnap.docs[0];
                Logger.debug('[acceptLoadForPO] Found single load using composite query:', { loadId: existingLoadDoc.id });
                querySucceeded = true;
                break;
              }
            } catch (compositeError: any) {
              // If index doesn't exist, fall back to poNumber-only query
              if (compositeError?.code === 'failed-precondition' || compositeError?.message?.includes('index')) {
                Logger.debug('[acceptLoadForPO] Composite index not available, using poNumber-only query');
              } else {
                throw compositeError; // Re-throw if it's not an index error
              }
            }
            
            // Fallback: Query by poNumber only, then filter in memory
            const loadsQuery = query(
              collection(getFailoverAwareDatabase(), 'loads'),
              where('poNumber', '==', poData.poNumber)
            );
            const loadsSnap = await getDocs(loadsQuery);
            
            // PRODUCTION FIX: Find load with matching carrierId OR no carrierId (to handle loads created before carrierId was set)
            existingLoadDoc = loadsSnap.docs.find(doc => {
              const data = doc.data();
              return data.carrierId === carrierId || !data.carrierId;
            });
            
            if (existingLoadDoc) {
              const loadData = existingLoadDoc.data();
              if (loadData.carrierId === carrierId) {
                Logger.debug('[acceptLoadForPO] Found single load with matching carrierId:', { loadId: existingLoadDoc.id });
              } else {
                Logger.debug('[acceptLoadForPO] Found load without carrierId, will update it:', { loadId: existingLoadDoc.id });
              }
              querySucceeded = true;
            } else if (!loadsSnap.empty) {
              // If no load found with matching carrierId or no carrierId, use the first load anyway
              // This handles edge cases where carrierId was set incorrectly
              existingLoadDoc = loadsSnap.docs[0];
              const loadData = existingLoadDoc.data();
              Logger.warn('[acceptLoadForPO] Found load(s) with different carrierId, will update first one:', {
                poNumber: poData.poNumber,
                expectedCarrierId: carrierId,
                foundCarrierId: loadData.carrierId,
                loadId: existingLoadDoc.id,
                loadCount: loadsSnap.size
              });
              querySucceeded = true;
            } else {
              querySucceeded = true; // Query succeeded, no loads found
            }
          } catch (queryError: any) {
            Logger.warn(`[acceptLoadForPO] Error querying for single load (attempt ${queryAttempts}/${maxQueryAttempts}):`, queryError as Error);
            
            // If it's a network error and we have retries left, wait and retry
            if (queryAttempts < maxQueryAttempts && (
              queryError?.code === 'unavailable' || 
              queryError?.code === 'deadline-exceeded' ||
              queryError?.message?.includes('network')
            )) {
              const retryDelay = Math.min(1000 * Math.pow(2, queryAttempts - 1), 5000); // Exponential backoff, max 5s
              Logger.debug(`[acceptLoadForPO] Retrying query after ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            } else {
              // Not a retryable error or out of retries
              querySucceeded = true; // Stop retrying
            }
          }
        }
      }
      
      if (existingLoadDoc) {
        // Update existing load document
        const loadDocRef = doc(getFailoverAwareDatabase(), 'loads', existingLoadDoc.id);
        const loadData = existingLoadDoc.data();
        // Validate that we're not overwriting with the wrong carrierId
        if (loadData.carrierId && loadData.carrierId !== carrierId) {
          Logger.warn('[acceptLoadForPO] Load already has different carrierId:', { 
            existing: loadData.carrierId, 
            new: carrierId 
          });
        }
        // Merge all relevant PO fields into the load, matching UI expectations
        const pickupLocation = poData.pickupLocation || poData.vendorInfo || {};
        const deliveryLocation = poData.deliveryLocation || poData.shipTo || {};
        const pickup = {
          location: pickupLocation.streetAddress || pickupLocation.address || '',
          time: poData.date || pickupLocation.date || '',
          status: LOAD_STATUS.PENDING,
        };
        const delivery = {
          location: deliveryLocation.streetAddress || deliveryLocation.address || '',
          time: poData.date || deliveryLocation.date || '',
          status: LOAD_STATUS.PENDING,
        };
        const updateFields: any = {
          carrierId: carrierId,
          isMarketplace: false, // Always set to false when assigning a carrier
          shippingScheduleStatus: newShippingScheduleStatus,
          updatedAt: serverTimestamp(),
          status: PO_STATUS.ACTIVE, // PRODUCTION FIX: ALWAYS set to 'Active' when shipper accepts (overrides any previous status)
          poNumber: poData.poNumber || '', // FIX: Use child PO number
          shipperId: poData.userId || '',
          shipper: poData.companyInfo?.name || poData.vendorInfo?.name || '',
          date: poData.date || poData.pickupLocation?.date || new Date().toISOString().split('T')[0], // PRODUCTION FIX: Required for Driver Updates orderBy query
          pickup, // for UI
          delivery, // for UI
          payment: counterOfferAmount || poData.rate || 0, // FIX: Counter offer rate for Driver Updates
          pickupLocation, // for backward compatibility
          deliveryLocation, // for backward compatibility
          rate: counterOfferAmount || poData.rate || 0, // FIX: Counter offer rate for Driver Updates
          weight: poData.items?.reduce((sum: number, item: any) => sum + (item.weight || 0), 0) || '',
          dimensions: poData.items && poData.items.length > 0
            ? `${poData.items[0].length || ''}x${poData.items[0].width || ''}x${poData.items[0].height || ''}`
            : '',
          ...multiLoadFields, // Add multi-load fields if this is a child PO
          // FIX: Set approvedCarrier for marketplace loads after acceptance
          approvedCarrier: carrierInfo
        };
        
        // Phase 3: Rate confirmation acceptance — only set if not already set (idempotency)
        if (rateConfirmationAcceptance && !loadData[RATE_CONFIRMATION_ACCEPTED_AT]) {
          updateFields[RATE_CONFIRMATION_ACCEPTED_AT] = rateConfirmationAcceptance.acceptedAt;
          updateFields[RATE_CONFIRMATION_ACCEPTED_BY] = rateConfirmationAcceptance.acceptedBy;
          if (rateConfirmationAcceptance.signatureName != null && rateConfirmationAcceptance.signatureName !== '') {
            updateFields[RATE_CONFIRMATION_SIGNATURE_NAME] = rateConfirmationAcceptance.signatureName;
          }
        }
        
        if (!updateFields.shipperId) {
          Logger.error('[acceptLoadForPO] Missing shipperId for load update:', new Error('Missing shipperId'), updateFields);
          throw new Error('Missing shipperId for load update');
        }
        Logger.debug('[acceptLoadForPO] Updating existing load document for PO:', { poNumber: poData.poNumber });
        Logger.debug('[acceptLoadForPO] Update fields:', {
          carrierId: updateFields.carrierId,
          status: updateFields.status,
          poNumber: updateFields.poNumber,
          shipperId: updateFields.shipperId
        });
        
        // PRODUCTION FIX: Log current load status before update
        const currentLoadData = existingLoadDoc.data();
        Logger.debug('[acceptLoadForPO] 🔍 BEFORE UPDATE - Current load status:', {
          loadId: existingLoadDoc.id,
          currentStatus: currentLoadData.status,
          currentCarrierId: currentLoadData.carrierId,
          poNumber: poData.poNumber,
          willUpdateTo: updateFields.status
        });
        
        await updateDoc(loadDocRef, updateFields);
        loadCreatedOrUpdated = true;
        createdLoadId = existingLoadDoc.id;
        Logger.debug('[acceptLoadForPO] Load update successful, loadId:', { loadId: createdLoadId });
        
        // PRODUCTION FIX: Immediately verify the update worked
        try {
          const immediateVerifySnap = await getDoc(loadDocRef);
          if (immediateVerifySnap.exists()) {
            const immediateData = immediateVerifySnap.data();
            Logger.debug('[acceptLoadForPO] ✅ IMMEDIATE VERIFY - Load status after update:', {
              loadId: existingLoadDoc.id,
              status: immediateData.status,
              carrierId: immediateData.carrierId,
              poNumber: immediateData.poNumber,
              statusMatches: immediateData.status === LOAD_STATUS.ACTIVE
            });
            
            // If status is still not 'Active', force fix it immediately
            if (immediateData.status !== LOAD_STATUS.ACTIVE) {
              Logger.error('[acceptLoadForPO] ❌ STATUS MISMATCH - Force fixing to Active NOW');
              await updateDoc(loadDocRef, {
                status: LOAD_STATUS.ACTIVE,
                updatedAt: serverTimestamp()
              });
              Logger.error('[acceptLoadForPO] ✅ Force fix applied');
            }
          }
        } catch (verifyErr) {
          Logger.error('[acceptLoadForPO] Error in immediate verify:', verifyErr as Error);
        }
        
        // CRITICAL: Verify the load can be queried by carrier after update
        try {
          const verifyCarrierQuery = query(
            collection(getFailoverAwareDatabase(), 'loads'),
            where('carrierId', '==', carrierId),
            where('poNumber', '==', poData.poNumber)
          );
          const verifyCarrierSnap = await getDocs(verifyCarrierQuery);
          if (verifyCarrierSnap.empty) {
            Logger.error('[acceptLoadForPO] CRITICAL: Load not found by carrier query after update!', new Error('Load not found by carrier query'), {
              carrierId,
              poNumber: poData.poNumber,
              loadId: createdLoadId
            });
          } else {
            const verifyLoadData = verifyCarrierSnap.docs[0].data();
            Logger.debug('[acceptLoadForPO] ✅ Load verified queryable by carrier:', {
              carrierId: verifyLoadData.carrierId,
              status: verifyLoadData.status,
              poNumber: verifyLoadData.poNumber
            });
          }
        } catch (verifyError) {
          Logger.error('[acceptLoadForPO] Error verifying carrier query:', verifyError as Error);
        }
        
        // PRODUCTION FIX: Post-update auto-fix verification - read back and force-fix if status is wrong
        try {
          const verifyLoadSnap = await getDoc(loadDocRef);
          if (verifyLoadSnap.exists()) {
            const verifyLoadData = verifyLoadSnap.data();
            // Auto-fix: If status is not 'Active', force update it
            if (verifyLoadData.status !== LOAD_STATUS.ACTIVE) {
              Logger.warn('[acceptLoadForPO] Load status is not Active after update:', { status: verifyLoadData.status });
              await updateDoc(loadDocRef, {
                status: LOAD_STATUS.ACTIVE,
                updatedAt: serverTimestamp()
              });
              Logger.debug('[acceptLoadForPO] Auto-fixed load status to Active');
            }
            // Verify carrierId matches
            if (verifyLoadData.carrierId !== carrierId) {
              Logger.warn('[acceptLoadForPO] Load carrierId mismatch after update - Auto-fixing');
              await updateDoc(loadDocRef, {
                carrierId: carrierId,
                updatedAt: serverTimestamp()
              });
              Logger.debug('[acceptLoadForPO] Auto-fixed load carrierId');
            }
          }
        } catch (verifyError) {
          Logger.error('[acceptLoadForPO] Error in post-update verification:', verifyError as Error);
          // Don't throw - update already succeeded, verification is safety check
        }
        
        // FIX 4 & 5: Update marketplace parent load remainingLoads if this was a multi-load allocation
        if (isMultiLoadParent && remainingLoads !== undefined && originalParentPONumber) {
          // Find the parent marketplace load (by parent PO number, not child)
          const parentLoadQuery = query(
            collection(getFailoverAwareDatabase(), 'loads'),
            where('poNumber', '==', originalParentPONumber),
            where('isMultiLoad', '==', true)
          );
          const parentLoadSnap = await getDocs(parentLoadQuery);
          
          if (!parentLoadSnap.empty) {
            const parentLoadRef = doc(getFailoverAwareDatabase(), 'loads', parentLoadSnap.docs[0].id);
            const updateData: any = {
              remainingLoads: remainingLoads,
              updatedAt: serverTimestamp()
            };
            
            if (remainingLoads === 0) {
              // All loads filled - remove from marketplace
              updateData.isMarketplace = false;
            }
            
            await updateDoc(parentLoadRef, updateData);
            Logger.debug('[acceptLoadForPO] Updated marketplace parent load remainingLoads:', { remainingLoads });
          }
        }
        
        // FIX 6: Also update any marketplace load documents that might still have 'Open' status
        // This ensures the load attached to the carrier shows 'Active' instead of 'Open'
        const marketplaceLoadsQuery = query(
          collection(getFailoverAwareDatabase(), 'loads'),
          where('poNumber', '==', poData.poNumber),
          where('status', '==', PO_STATUS.OPEN)
        );
        const marketplaceLoadsSnap = await getDocs(marketplaceLoadsQuery);
        
        if (!marketplaceLoadsSnap.empty) {
          for (const marketplaceLoadDoc of marketplaceLoadsSnap.docs) {
            const marketplaceLoadData = marketplaceLoadDoc.data();
            // Only update if this load is for the same carrier or is a marketplace load
            if (!marketplaceLoadData.carrierId || marketplaceLoadData.carrierId === carrierId) {
              await updateDoc(doc(getFailoverAwareDatabase(), 'loads', marketplaceLoadDoc.id), {
                status: PO_STATUS.ACTIVE, // FIX: Change from 'Open' to 'Active'
                carrierId: carrierId,
                isMarketplace: false,
                shippingScheduleStatus: SHIPPING_SCHEDULE_STATUS.ACTIVE,
                approvedCarrier: carrierInfo,
                rate: counterOfferAmount || marketplaceLoadData.rate || 0,
                payment: counterOfferAmount || marketplaceLoadData.payment || 0,
                date: poData.date || marketplaceLoadData.date || new Date().toISOString().split('T')[0], // PRODUCTION FIX: Ensure date field exists for Driver Updates query
                updatedAt: serverTimestamp()
              });
              Logger.debug('[acceptLoadForPO] Updated marketplace load from Open to Active:', { loadId: marketplaceLoadDoc.id });
            }
          }
        }

      } else {
        // Load not found - this is a critical error that must be fixed at the source
        // The load SHOULD have been created when the partner request was sent
        // Do NOT create a new load - this masks the real problem
        
        // Try one more comprehensive search to find any load with this PO number
        // (might exist but without carrierId set yet)
        let foundLoadWithoutCarrierId = null;
        try {
          const comprehensiveQuery = query(
            collection(getFailoverAwareDatabase(), 'loads'),
            where('poNumber', '==', poData.poNumber)
          );
          const comprehensiveSnap = await getDocs(comprehensiveQuery);
          
          if (!comprehensiveSnap.empty) {
            const foundLoadsData = comprehensiveSnap.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                carrierId: data.carrierId,
                status: data.status
              };
            });
            Logger.error('[acceptLoadForPO] CRITICAL: Load not found with matching carrierId:', new Error('Load not found with matching carrierId'), {
              poNumber: poData.poNumber,
              expectedCarrierId: carrierId,
              foundLoads: foundLoadsData
            });
            
            // Check if there's a load without carrierId that we can update
            foundLoadWithoutCarrierId = comprehensiveSnap.docs.find(doc => {
              const data = doc.data();
              return !data.carrierId; // Only update loads that have NO carrierId (not wrong carrierId)
            });
            
            if (foundLoadWithoutCarrierId) {
              Logger.debug('[acceptLoadForPO] Found load without carrierId, updating it:', { loadId: foundLoadWithoutCarrierId.id });
              const loadDocRef = doc(getFailoverAwareDatabase(), 'loads', foundLoadWithoutCarrierId.id);
              const loadData = foundLoadWithoutCarrierId.data();
              
              const pickupLocation = poData.pickupLocation || poData.vendorInfo || {};
              const deliveryLocation = poData.deliveryLocation || poData.shipTo || {};
              const pickup = {
                location: pickupLocation.streetAddress || pickupLocation.address || '',
                time: poData.date || pickupLocation.date || '',
                status: LOAD_STATUS.PENDING,
              };
              const delivery = {
                location: deliveryLocation.streetAddress || deliveryLocation.address || '',
                time: poData.date || deliveryLocation.date || '',
                status: LOAD_STATUS.PENDING,
              };
              
              const updateFields: any = {
                carrierId: carrierId,
                isMarketplace: false,
                shippingScheduleStatus: newShippingScheduleStatus,
                updatedAt: serverTimestamp(),
                status: LOAD_STATUS.ACTIVE, // PRODUCTION FIX: ALWAYS set to 'Active' when shipper accepts (overrides any previous status)
                poNumber: poData.poNumber || '',
                shipperId: poData.userId || '',
                shipper: poData.companyInfo?.name || poData.vendorInfo?.name || '',
                date: poData.date || poData.pickupLocation?.date || new Date().toISOString().split('T')[0],
                pickup,
                delivery,
                payment: counterOfferAmount || poData.rate || 0,
                pickupLocation,
                deliveryLocation,
                rate: counterOfferAmount || poData.rate || 0,
                weight: poData.items?.reduce((sum: number, item: any) => sum + (item.weight || 0), 0) || '',
                dimensions: poData.items && poData.items.length > 0
                  ? `${poData.items[0].length || ''}x${poData.items[0].width || ''}x${poData.items[0].height || ''}`
                  : '',
                ...multiLoadFields,
                approvedCarrier: carrierInfo
              };
              if (rateConfirmationAcceptance && !loadData[RATE_CONFIRMATION_ACCEPTED_AT]) {
                updateFields[RATE_CONFIRMATION_ACCEPTED_AT] = rateConfirmationAcceptance.acceptedAt;
                updateFields[RATE_CONFIRMATION_ACCEPTED_BY] = rateConfirmationAcceptance.acceptedBy;
                if (rateConfirmationAcceptance.signatureName != null && rateConfirmationAcceptance.signatureName !== '') {
                  updateFields[RATE_CONFIRMATION_SIGNATURE_NAME] = rateConfirmationAcceptance.signatureName;
                }
              }
              Logger.debug('[acceptLoadForPO] Updating load found without carrierId, update fields:', {
                carrierId: updateFields.carrierId,
                status: updateFields.status,
                poNumber: updateFields.poNumber,
                shipperId: updateFields.shipperId
              });
              await updateDoc(loadDocRef, updateFields);
              loadCreatedOrUpdated = true;
              createdLoadId = foundLoadWithoutCarrierId.id;
              Logger.debug('[acceptLoadForPO] Updated existing load found without carrierId:', { loadId: createdLoadId });
              
              // Auto-fix verification
              try {
                const verifyLoadSnap = await getDoc(loadDocRef);
                if (verifyLoadSnap.exists()) {
                  const verifyLoadData = verifyLoadSnap.data();
                  if (verifyLoadData.status !== LOAD_STATUS.ACTIVE) {
                    await updateDoc(loadDocRef, { status: LOAD_STATUS.ACTIVE, updatedAt: serverTimestamp() });
                    Logger.debug('[acceptLoadForPO] Auto-fixed load status to Active');
                  }
                  if (verifyLoadData.carrierId !== carrierId) {
                    await updateDoc(loadDocRef, { carrierId: carrierId, updatedAt: serverTimestamp() });
                    Logger.debug('[acceptLoadForPO] Auto-fixed load carrierId');
                  }
                }
              } catch (verifyError) {
                Logger.error('[acceptLoadForPO] Error in verification:', verifyError as Error);
              }
              
              // Verify the load can be queried by carrier after update
              try {
                const verifyCarrierQuery = query(
                  collection(getFailoverAwareDatabase(), 'loads'),
                  where('carrierId', '==', carrierId),
                  where('poNumber', '==', poData.poNumber)
                );
                const verifyCarrierSnap = await getDocs(verifyCarrierQuery);
                if (verifyCarrierSnap.empty) {
                  Logger.error('[acceptLoadForPO] CRITICAL: Load not found by carrier query after update!', new Error('Load not found by carrier query'), {
                    carrierId,
                    poNumber: poData.poNumber,
                    loadId: createdLoadId
                  });
                } else {
                  const verifyLoadData = verifyCarrierSnap.docs[0].data();
                  Logger.debug('[acceptLoadForPO] ✅ Load verified queryable by carrier:', {
                    carrierId: verifyLoadData.carrierId,
                    status: verifyLoadData.status,
                    poNumber: verifyLoadData.poNumber
                  });
                }
              } catch (verifyError) {
                Logger.error('[acceptLoadForPO] Error verifying carrier query:', verifyError as Error);
              }
              
              // Exit early - we found and updated the load
              return;
            }
          }
        } catch (comprehensiveError) {
          Logger.error('[acceptLoadForPO] Error in comprehensive search:', comprehensiveError as Error);
        }
        
        // If we get here, the load truly doesn't exist - this is a critical error
        // The load should have been created when the partner request was sent
        if (!loadCreatedOrUpdated) {
          const errorMessage = `Load not found for PO ${poData.poNumber} with carrierId ${carrierId}. The load should have been created when the partner request was sent. This indicates a workflow failure that must be fixed at the source.`;
          Logger.error('[acceptLoadForPO] CRITICAL ERROR:', new Error(errorMessage), { message: errorMessage });
          Logger.debug('[acceptLoadForPO] Debug info:', {
            poNumber: poData.poNumber,
            carrierId: carrierId,
            shipperId: poData.userId,
            poStatus: poData.status,
            foundLoadWithoutCarrierId: !!foundLoadWithoutCarrierId
          });
          throw new Error(errorMessage);
        }
      }
    }
    
    // If load was not created/updated, this is a critical error
    if (!loadCreatedOrUpdated && poData.poNumber) {
      const errorMessage = `Load was not created or updated for PO ${poData.poNumber}. The load should have been created when the partner request was sent. This indicates a workflow failure that must be fixed at the source.`;
      Logger.error('[acceptLoadForPO] CRITICAL ERROR:', new Error(errorMessage), { message: errorMessage });
      Logger.error('[acceptLoadForPO] This will cause the load to not appear in carrier My Loads page');
      throw new Error(errorMessage);
    }
  } catch (err) {
    Logger.error('[acceptLoadForPO] Error updating load with carrierId:', err as Error);
    Logger.error('[acceptLoadForPO] Error details:', err as Error, {
      message: err instanceof Error ? err.message : String(err),
      code: (err as any)?.code,
      stack: err instanceof Error ? err.stack : undefined,
      poNumber: poData.poNumber,
      carrierId: carrierId,
      loadCreatedOrUpdated: loadCreatedOrUpdated
    });
    
    // If load was not created/updated, this is a critical error
    // Do NOT create a new load - this masks the real problem
    if (!loadCreatedOrUpdated && poData.poNumber) {
      const errorMessage = `Load was not created or updated for PO ${poData.poNumber} due to error: ${err instanceof Error ? err.message : String(err)}. The load should have been created when the partner request was sent. This indicates a workflow failure that must be fixed at the source.`;
      Logger.error('[acceptLoadForPO] CRITICAL ERROR:', new Error(errorMessage), { message: errorMessage });
      throw new Error(errorMessage);
    }
    
    // If load was already created/updated, check if this is a permissions error
    // If so, don't throw as the load was already accepted
    if (loadCreatedOrUpdated) {
      if (err instanceof Error && err.message.includes('Missing or insufficient permissions')) {
        Logger.warn('[acceptLoadForPO] Load update failed due to permissions but load was already accepted - continuing workflow');
      } else {
        // For other errors after load was created, log but don't throw
        Logger.warn('[acceptLoadForPO] Error occurred but load was already created/updated - continuing workflow');
      }
    }
  }
  
  
  // Log final status
  if (loadCreatedOrUpdated) {
    Logger.debug('[acceptLoadForPO] ✅ Load successfully created/updated, loadId:', { loadId: createdLoadId });
    
    // FINAL VERIFICATION: Ensure load has correct status and carrierId
    // This catches any edge cases where status might not be set correctly
    if (createdLoadId && poData.poNumber) {
      try {
        const finalLoadRef = doc(getFailoverAwareDatabase(), 'loads', createdLoadId);
        const finalLoadSnap = await getDoc(finalLoadRef);
        
        if (finalLoadSnap.exists()) {
          const finalLoadData = finalLoadSnap.data();
          let needsUpdate = false;
          const updateFields: any = {};
          
          // PRODUCTION FIX: Verify status is 'Active' - CRITICAL for carrier dashboard visibility
          // Handle case-insensitive comparison and normalize to 'Active'
          const currentStatus = (finalLoadData.status || '').toString().trim();
          const statusLower = currentStatus.toLowerCase();
          const isActive = statusLower === 'active' || currentStatus === LOAD_STATUS.ACTIVE;
          
          if (!isActive) {
            Logger.error('[acceptLoadForPO] ❌ FINAL VERIFICATION FAILED: Load status is not Active:', new Error('Final verification failed'), {
              loadId: createdLoadId,
              currentStatus: currentStatus,
              statusType: typeof finalLoadData.status,
              expectedStatus: LOAD_STATUS.ACTIVE,
              poNumber: poData.poNumber,
              carrierId: finalLoadData.carrierId,
              willForceFix: true
            });
            updateFields.status = LOAD_STATUS.ACTIVE;
            needsUpdate = true;
          } else {
            Logger.debug('[acceptLoadForPO] ✅ FINAL VERIFICATION PASSED: Load status is Active:', {
              loadId: createdLoadId,
              status: currentStatus,
              poNumber: poData.poNumber
            });
          }
          
          // Verify carrierId matches
          if (finalLoadData.carrierId !== carrierId) {
            Logger.warn('[acceptLoadForPO] FINAL VERIFICATION: Load carrierId mismatch:', {
              expected: carrierId,
              actual: finalLoadData.carrierId
            });
            updateFields.carrierId = carrierId;
            needsUpdate = true;
          }
          
          if (needsUpdate) {
            updateFields.updatedAt = serverTimestamp();
            await updateDoc(finalLoadRef, updateFields);
            Logger.debug('[acceptLoadForPO] FINAL VERIFICATION: Fixed load status and/or carrierId');
          } else {
            Logger.debug('[acceptLoadForPO] FINAL VERIFICATION: Load has correct status (Active) and carrierId');
          }
        }
      } catch (verifyError) {
        Logger.error('[acceptLoadForPO] Error in final verification:', verifyError as Error);
        // Don't throw - this is just a safety check
      }
    }
  } else {
    Logger.error('[acceptLoadForPO] ❌ Load was NOT created/updated - carrier will not see load in My Loads page');
  }

  // 7. Update or create shipping schedule document for ALL carriers (both partnered and marketplace)
  try {
    // Validate required fields before creating shipping schedule
    if (!poData.userId || !carrierId || !poData.poNumber) {
      Logger.error('[acceptLoadForPO] Missing required fields for shipping schedule creation:', new Error('Missing required fields'), {
        shipperId: poData.userId,
        carrierId: carrierId,
        poNumber: poData.poNumber
      });
      throw new Error('Missing required fields for shipping schedule creation');
    }

    // Check if shipping schedule document already exists (prevent duplicates)
    const existingScheduleQuery = query(
      collection(getFailoverAwareDatabase(), 'shippingSchedules'),
      where('poNumber', '==', poData.poNumber),
      where('carrierId', '==', carrierId)
    );
    const existingScheduleSnap = await getDocs(existingScheduleQuery);
    
    const shippingScheduleData: any = {
      poNumber: poData.poNumber,
      userId: poData.userId,
      shipperId: poData.userId,
      carrierId: carrierId,
      shippingScheduleStatus: newShippingScheduleStatus, // FIX: Now 'Active' for both marketplace and partnered
      date: poData.date || new Date().toISOString().split('T')[0],
      scheduledTime: poData.scheduledTime || 'TBD',
      shipTo: poData.shipTo || {},
      vendorInfo: poData.vendorInfo || {},
      pickupLocation: poData.pickupLocation || {},
      deliveryLocation: poData.deliveryLocation || {},
      rate: counterOfferAmount || poData.rate || 0,
      total: counterOfferAmount || poData.total || poData.rate || 0,
      carrierOption: 'carrier',
      selectedCarrier: carrierInfo,
      type: isActuallyPartnered ? (poData.type || 'Full Load') : 'Marketplace',
      carrier: isActuallyPartnered ? carrierProfile.companyName || 'Partner Carrier' : 'Marketplace',
      updatedAt: serverTimestamp()
    };
    
    if (!existingScheduleSnap.empty) {
      // Update existing shipping schedule document
      const existingScheduleDoc = existingScheduleSnap.docs[0];
      await updateDoc(doc(getFailoverAwareDatabase(), 'shippingSchedules', existingScheduleDoc.id), shippingScheduleData);
      Logger.debug('[acceptLoadForPO] Updated existing shipping schedule document:', { scheduleId: existingScheduleDoc.id });
    } else {
      // Create new shipping schedule document only if it doesn't exist
      shippingScheduleData.createdAt = serverTimestamp();
      await addDoc(collection(getFailoverAwareDatabase(), 'shippingSchedules'), shippingScheduleData);
      Logger.debug('[acceptLoadForPO] Created new shipping schedule document');
    }
    
  } catch (err) {
    Logger.error('[acceptLoadForPO] Error updating/creating shipping schedule document:', err as Error);
    // Don't throw error here as the main workflow should still succeed
  }

  // 7a. Update partner request status to 'accepted' to remove from Available Loads page
  try {
    const partnerRequestQuery = query(
      collection(getFailoverAwareDatabase(), 'partnerRequests'),
      where('poNumber', '==', poData.poNumber),
      where('carrierId', '==', carrierId)
    );
    const partnerRequestSnap = await getDocs(partnerRequestQuery);
    
    if (!partnerRequestSnap.empty) {
      const updatePromises = partnerRequestSnap.docs.map(partnerRequestDoc =>
        updateDoc(doc(getFailoverAwareDatabase(), 'partnerRequests', partnerRequestDoc.id), {
          status: PARTNER_REQUEST_STATUS.ACCEPTED,
          updatedAt: serverTimestamp(),
          acceptedAt: serverTimestamp()
        })
      );
      await Promise.all(updatePromises);
      Logger.debug('[acceptLoadForPO] Updated', { count: partnerRequestSnap.size });
    }
  } catch (err) {
    Logger.error('[acceptLoadForPO] Error updating partner request status:', err as Error);
    // Don't throw error here as the main workflow should still succeed
  }

  // 8. Create shipment document for Driver Updates page (FIX: Now for both marketplace and partnered)
  // After shipper accepts, both marketplace and partnered loads should appear in Driver Updates
  {
    try {
      // Validate required fields before creating shipment
      if (!poData.userId || !carrierId || !poData.poNumber) {
        Logger.error('[acceptLoadForPO] Missing required fields for shipment creation:', new Error('Missing required fields'), {
          shipperId: poData.userId,
          carrierId: carrierId,
          poNumber: poData.poNumber
        });
        throw new Error('Missing required fields for shipment creation');
      }

      const shipmentData = {
        shipperId: poData.userId,
        carrierId: carrierId,
        poNumber: poData.poNumber,
        status: LOAD_STATUS.IN_PROGRESS, // This status will show in Driver Updates page
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        pickup: {
          location: poData.pickupLocation?.streetAddress || poData.vendorInfo?.address || 'Unknown Pickup',
          time: poData.date || new Date().toISOString(),
          status: LOAD_STATUS.PENDING,
        },
        delivery: {
          location: poData.deliveryLocation?.streetAddress || poData.shipTo?.address || 'Unknown Delivery',
          time: poData.date || new Date().toISOString(),
          status: LOAD_STATUS.PENDING,
        },
        // Add coordinates for progress calculation and location display
        pickupLocation: poData.pickupLocation || poData.vendorInfo || {},
        deliveryLocation: poData.deliveryLocation || poData.shipTo || {},
        payment: counterOfferAmount || poData.rate || 0, // FIX: Use counter offer rate if available
        weight: poData.items?.reduce((sum: number, item: any) => sum + (item.weight || 0), 0) || 0,
        dimensions: poData.items && poData.items.length > 0
          ? `${poData.items[0].length || ''}x${poData.items[0].width || ''}x${poData.items[0].height || ''}`
          : '',
        shipper: poData.companyInfo?.name || poData.vendorInfo?.name || 'Unknown Shipper',
        carrier: {
          id: carrierId,
          companyName: carrierProfile.companyName || 'Unknown Carrier',
          email: carrierProfile.email || '',
          phoneNumber: carrierProfile.phoneNumber || ''
        },
      };

      await addDoc(collection(getFailoverAwareDatabase(), 'shipments'), shipmentData);
      
    } catch (err) {
      Logger.error('[acceptLoadForPO] Error creating shipment document:', err as Error);
      // Don't throw error here as the main workflow should still succeed
    }
  }

  scheduleLocationVisibilityRefresh(carrierId);
};

// New poNumber-driven functions
const handleLoadRejection = async (poNumber: string, notificationId: string) => {

  try {
    // Read the notification to determine marketplace vs partner flow
    const notifSnap = await getDoc(doc(getFailoverAwareDatabase(), 'notifications', notificationId));
    const notifData = notifSnap.data() as LoadRequestNotification | undefined;
    const isMarketplaceOffer = (notifData?.type === NOTIFICATION_TYPES.MAKE_OFFER) || (notifData as any)?.loadDetails?.isMarketplace === true;

    // 1. Update load status and flags appropriately
    const loadsQuery = query(collection(getFailoverAwareDatabase(), 'loads'), where('poNumber', '==', poNumber));
    const loadsSnap = await getDocs(loadsQuery);

    for (const loadDoc of loadsSnap.docs) {
      const update: any = {
        status: LOAD_STATUS.PENDING,
        carrierId: null,
        updatedAt: serverTimestamp(),
      };
      if (isMarketplaceOffer) {
        // Put the load back on the marketplace
        update.isMarketplace = true;
      }
      await updateDoc(doc(getFailoverAwareDatabase(), 'loads', loadDoc.id), update);
    }

    // 2. Check if this is a broker-managed load first
    const brokerPoQuery = query(collection(getFailoverAwareDatabase(), 'brokerPurchaseOrders'), where('poNumber', '==', poNumber));
    const brokerPoSnap = await getDocs(brokerPoQuery);
    
    if (!brokerPoSnap.empty) {
      // This is a broker-managed load
      const brokerPoData = brokerPoSnap.docs[0].data();
      const brokerId = brokerPoData.brokerId;
      
      if (brokerId) {
        // Create notification record for broker (broker will handle PO update)
        const brokerNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
        await addDoc(brokerNotificationRef, {
          brokerId: brokerId,
          recipientId: brokerId,
          poNumber: poNumber,
          status: PARTNER_REQUEST_STATUS.DECLINED,
          type: NOTIFICATION_TYPES.CARRIER_DECLINE,
          message: `Carrier declined load for PO ${poNumber}. Please select a new carrier.`,
          rejectionReason: 'Carrier rejected the load',
          read: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          requiresAction: true
        });

      }
    } else {
      // This is a shipper-managed load - get shipperId from purchase order
      const poQuery = query(collection(getFailoverAwareDatabase(), 'purchaseOrders'), where('poNumber', '==', poNumber));
      const poSnap = await getDocs(poQuery);

      let shipperId: string | null = null;
      
      if (!poSnap.empty) {
        const poData = poSnap.docs[0].data();
        shipperId = poData.userId; // Get correct shipperId from purchase order using userId

      }

      // Create notification record for shipper using correct shipperId from PO
      if (shipperId) {
        const shipperNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
        await addDoc(shipperNotificationRef, {
          shipperId: shipperId,
          recipientId: shipperId,
          poNumber: poNumber,
          status: PARTNER_REQUEST_STATUS.DECLINED,
          type: NOTIFICATION_TYPES.CARRIER_DECLINE,
          message: `Carrier declined load for PO ${poNumber}. Please select a new carrier.`,
          read: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          requiresAction: true
        });

      } else {
        Logger.warn('[handleLoadRejection] No shipperId found in purchase order data for poNumber:', { poNumber });
      }
    }
    
  } catch (err) {
    Logger.error('[handleLoadRejection] Error:', err as Error);
    throw new Error(`Failed to handle load rejection: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
};

const handleLoadAcceptance = async (poNumber: string, notificationId: string) => {
  Logger.debug('[handleLoadAcceptance] Starting for PO:', { poNumber, notificationId });
  try {
    // Get notification data for carrier info
    const notificationDoc = await getDoc(doc(getFailoverAwareDatabase(), 'notifications', notificationId));
    const notifData = notificationDoc.data() as LoadRequestNotification;
    
    Logger.debug('[handleLoadAcceptance] Notification data:', {
      poNumber,
      carrierId: notifData?.carrierId,
      userType: notifData?.userType,
      brokerId: notifData?.brokerId,
      hasNotification: !!notifData
    });
    
    if (!notifData?.carrierId) {
      throw new Error('No carrierId found in notification data');
    }

    // Get counter offer amount from notification if it exists
    const counterOfferAmount = notifData.loadDetails?.rate;

    // Check if this is a broker load or regular shipper load
    // Fallback: if userType is not set, check if brokerId exists
    const isBrokerLoad = notifData.userType === USER_TYPES.BROKER || !!notifData.brokerId;

    if (isBrokerLoad) {
      // Import and use broker-specific acceptance function
      const { acceptLoadForBrokerPO } = await import('./brokerService');
      
      // Check if carrier is actually partnered with broker
      const brokerId = notifData.userId || notifData.brokerId;
      if (!brokerId) {
        Logger.error('[handleLoadAcceptance] No broker ID found in notification data');
        throw new Error('No broker ID found for partnership check');
      }
      const isActuallyPartnered = await checkCarrierPartnership(brokerId, notifData.carrierId);

      await acceptLoadForBrokerPO(poNumber, notifData.carrierId, isActuallyPartnered, counterOfferAmount);

    } else {
      // Use the complete acceptLoadForPO function for regular shipper loads
      await acceptLoadForPO(poNumber, notifData.carrierId, false, counterOfferAmount);
    }

    // Create notification record for shipper/broker
    const recipientId = notifData.shipperId || notifData.userId;
    if (recipientId) {
      const recipientNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
      await addDoc(recipientNotificationRef, {
        shipperId: recipientId,
        recipientId: recipientId,
        poNumber: poNumber,
        status: NOTIFICATION_STATUS.ACCEPTED,
        type: isBrokerLoad ? NOTIFICATION_TYPES.CARRIER_ACCEPT_BROKER : NOTIFICATION_TYPES.CARRIER_ACCEPT,
        message: `Carrier accepted load for PO ${poNumber}`,
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

    }
    
  } catch (err) {
    Logger.error('[handleLoadAcceptance] Error:', err as Error);
    throw new Error(`Failed to handle load acceptance: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
};

export const handleCounterOffer = async (poNumber: string, notificationId: string, counterOffer: number) => {

  try {
    // Get notification data for carrier info
    const sourceNotificationDoc = await getDoc(doc(getFailoverAwareDatabase(), 'notifications', notificationId));
    const notifData = sourceNotificationDoc.data() as LoadRequestNotification;
    
    if (!notifData) {
      throw new Error(`Notification ${notificationId} not found`);
    }
    
    // Get effective PO number from notification (handles both single loads and multi-load child POs)
    // For single loads: poNumber is the PO number
    // For multi-load child POs: loadDetails.poNumber contains the child PO number
    const effectivePONumber = notifData.loadDetails?.poNumber || notifData.poNumber || poNumber;
    Logger.warn('[handleCounterOffer] Processing counter offer for PO:', {
      effectivePONumber,
      fromLoadDetails: notifData.loadDetails?.poNumber,
      fromNotification: notifData.poNumber,
      fromParameter: poNumber,
      isChildPO: notifData.loadDetails?.poNumber !== notifData.poNumber
    });
    
    // Query both collections to determine load type (works for both single loads and child POs)
    // No status filtering - works regardless of PO status (Processing, Active, etc.)
    const brokerPoQuery = query(
      collection(getFailoverAwareDatabase(), 'brokerPurchaseOrders'), 
      where('poNumber', '==', effectivePONumber)
    );
    const shipperPoQuery = query(
      collection(getFailoverAwareDatabase(), 'purchaseOrders'), 
      where('poNumber', '==', effectivePONumber)
    );
    
    const [brokerPoSnap, shipperPoSnap] = await Promise.all([
      getDocs(brokerPoQuery),
      getDocs(shipperPoQuery)
    ]);
    
    const isBrokerLoad = !brokerPoSnap.empty;
    const isShipperLoad = !shipperPoSnap.empty;
    
    // If PO not found in either collection, use notification data as fallback
    // This handles edge cases where PO might not be immediately available
    if (!isBrokerLoad && !isShipperLoad) {
      Logger.warn('[handleCounterOffer] PO not found in collections, using notification data as fallback');
      
      // Determine user type from notification structure (brokerId vs shipperId)
      const hasBrokerId = !!notifData.brokerId;
      const hasShipperId = !!notifData.shipperId;
      const notificationUserId = notifData.userId || notifData.shipperId || notifData.brokerId;
      
      if (!notificationUserId) {
        throw new Error(`No purchase order found for PO ${effectivePONumber} and no user ID available in notification`);
      }
      
      // Get carrier info
      const carrierId = notifData.carrierId;
      let carrierName = 'Unknown Carrier';
      if (carrierId) {
        try {
          const migrationDataAccess = new MigrationDataAccessLayer();
          const result = await migrationDataAccess.getUserProfile(carrierId);
          if (result.success && result.data) {
            const carrierData = result.data;
            carrierName = carrierData.companyName || carrierData.displayName || 'Unknown Carrier';
          }
        } catch (carrierError) {
          Logger.warn('[handleCounterOffer] Could not fetch carrier name:', carrierError as Error);
        }
      }
      
      // Create notification using notification data (determine type from structure, not hard-coded userType)
      const fallbackNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
      const fallbackNotificationData: any = {
        carrierId: carrierId,
        senderId: carrierId,
        senderName: carrierName,
        poNumber: effectivePONumber,
        status: NOTIFICATION_STATUS.COUNTER_OFFER,
        type: NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
        message: `Carrier made counter offer of $${counterOffer.toFixed(2)} for PO ${effectivePONumber}`,
        loadDetails: {
          ...(notifData.loadDetails || {}),
          rate: counterOffer,
          poNumber: effectivePONumber,
          carrierName: carrierName
        },
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        requiresAction: true
      };
      
      // Set appropriate ID fields based on notification structure (not hard-coded userType)
      if (hasBrokerId) {
        fallbackNotificationData.brokerId = notificationUserId;
        fallbackNotificationData.userId = notificationUserId;
        fallbackNotificationData.recipientId = notificationUserId;
      } else {
        // Default to shipper if brokerId not present
        fallbackNotificationData.shipperId = notificationUserId;
        fallbackNotificationData.userId = notificationUserId;
        fallbackNotificationData.recipientId = notificationUserId;
      }
      
      const fallbackNotificationDoc = await addDoc(fallbackNotificationRef, fallbackNotificationData);
      Logger.warn('[handleCounterOffer] ✅ Created counter offer notification using notification data fallback:', {
        notificationId: fallbackNotificationDoc.id,
        userId: notificationUserId,
        poNumber: effectivePONumber
      });
      return;
    }

    // Get carrier info for the notification
    const carrierId = notifData.carrierId;
    let carrierName = 'Unknown Carrier';
    if (carrierId) {
      try {
        const migrationDataAccess = new MigrationDataAccessLayer();
        const result = await migrationDataAccess.getUserProfile(carrierId);
        if (result.success && result.data) {
          const carrierData = result.data;
          carrierName = carrierData.companyName || carrierData.displayName || 'Unknown Carrier';
        }
      } catch (carrierError) {
        Logger.warn('[handleCounterOffer] Could not fetch carrier name:', carrierError as Error);
      }
    }

    // Extract user ID from PO data (works for both single loads and child POs)
    // Both single loads and child POs have the same structure: userId or shipperId/brokerId
    let userId: string;
    let isBroker: boolean;
    
    if (isBrokerLoad) {
      const brokerPoData = brokerPoSnap.docs[0].data();
      userId = brokerPoData.brokerId || brokerPoData.userId;
      isBroker = true;
      
      if (!userId) {
        throw new Error(`No brokerId or userId found in broker purchase order for PO ${effectivePONumber}`);
      }
    } else {
      const shipperPoData = shipperPoSnap.docs[0].data();
      userId = shipperPoData.userId || shipperPoData.shipperId;
      isBroker = false;
      
      if (!userId) {
        throw new Error(`No userId or shipperId found in purchase order for PO ${effectivePONumber}`);
      }
    }
    
    // Create notification (same structure for both single loads and child POs)
    const notificationRef = collection(getFailoverAwareDatabase(), 'notifications');
    const notificationData: any = {
      carrierId: carrierId,
      senderId: carrierId,
      senderName: carrierName,
      poNumber: effectivePONumber,
      status: NOTIFICATION_STATUS.COUNTER_OFFER,
      type: NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
      message: `Carrier made counter offer of $${counterOffer.toFixed(2)} for PO ${effectivePONumber}`,
      loadDetails: {
        ...(notifData.loadDetails || {}),
        rate: counterOffer,
        poNumber: effectivePONumber,
        carrierName: carrierName
      },
      read: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      requiresAction: true,
      userId: userId,
      recipientId: userId
    };
    
    // Set type-specific ID field (brokerId or shipperId) based on load type
    // CRITICAL: Set both brokerId/shipperId AND userId/recipientId to ensure notifications are found by all queries
    if (isBroker) {
      notificationData.brokerId = userId;
      notificationData.userId = userId; // Also set userId for fallback queries
      notificationData.recipientId = userId; // Ensure recipientId matches
    } else {
      notificationData.shipperId = userId;
      notificationData.userId = userId; // Also set userId for fallback queries
      notificationData.recipientId = userId; // Ensure recipientId matches
    }
    
    const notificationDoc = await addDoc(notificationRef, notificationData);
    
    Logger.warn('[handleCounterOffer] ✅ Created counter offer notification:', {
      notificationId: notificationDoc.id,
      userId: userId,
      brokerId: isBroker ? userId : undefined,
      shipperId: isBroker ? undefined : userId,
      recipientId: userId,
      isBroker: isBroker,
      poNumber: effectivePONumber,
      isChildPO: notifData.loadDetails?.poNumber !== notifData.poNumber,
      childPONumber: notifData.loadDetails?.poNumber,
      parentPONumber: notifData.poNumber,
      counterOffer: counterOffer,
      type: NOTIFICATION_TYPES.CARRIER_COUNTER_OFFER,
      status: NOTIFICATION_STATUS.COUNTER_OFFER
    });
    
  } catch (err) {
    Logger.error('[handleCounterOffer] Error:', err as Error);
    throw new Error(`Failed to handle counter offer: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
}; 

// Simple shipper rejection function - separate from broker logic
export const handleShipperRejection = async (
  poNumber: string,
  carrierId: string,
  shipperId: string
): Promise<void> => {
  try {

    // FIX: Check if this is a multi-load child PO and return it to marketplace
    const poQuery = query(
      collection(getFailoverAwareDatabase(), 'purchaseOrders'), 
      where('poNumber', '==', poNumber)
    );
    const poSnap = await getDocs(poQuery);
    
    let isChildPO = false;
    if (!poSnap.empty) {
      const poData = poSnap.docs[0].data();
      isChildPO = poData.isMultiLoad === true && poData.parentPONumber;
      
      // If this is a child PO, return it to marketplace (increment remainingLoads)
      if (isChildPO) {
        try {
          const { multiLoadService } = await import('./multiLoadService');
          const result = await multiLoadService.returnChildPOToMarketplace(poNumber, 'purchaseOrders');
          
          if (!result.success) {
            Logger.error('[handleShipperRejection] Failed to return child PO to marketplace:', new Error(result.error || 'Failed to return child PO'), { error: result.error });
            // Continue with regular rejection flow as fallback
          } else {
            Logger.debug('[handleShipperRejection] Child PO returned to marketplace, remainingLoads:', { remainingLoads: result.remainingLoads });
          }
        } catch (multiLoadError) {
          Logger.warn('[handleShipperRejection] Error returning child PO to marketplace:', multiLoadError as Error);
          // Continue with regular rejection flow as fallback
        }
      }
    }

    // 1. Update load status to 'Open' and remove carrierId so it goes back to marketplace
    const loadsQuery = query(
      collection(getFailoverAwareDatabase(), 'loads'), 
      where('poNumber', '==', poNumber)
    );
    const loadsSnap = await getDocs(loadsQuery);

    for (const loadDoc of loadsSnap.docs) {
      // Filter out parent marketplace loads - only update child-specific loads
      const loadData = loadDoc.data();
      if (!(loadData.isMultiLoad === true && !loadData.parentPONumber)) {
        await updateDoc(doc(getFailoverAwareDatabase(), 'loads', loadDoc.id), {
          status: LOAD_STATUS.PENDING, // Keep as pending for new carrier selection
          carrierId: null, // Remove rejected carrier
          isMarketplace: false, // Keep as partner load, NOT marketplace
          updatedAt: serverTimestamp(),
          rejectionReason: 'Carrier rejected by shipper',
          rejectedAt: serverTimestamp()
        });
      }
    }

    // 2. Update purchase order status - check if this is a partner load or marketplace load
    if (!poSnap.empty) {
      const poDoc = poSnap.docs[0];
      const poData = poDoc.data();
      
      // Check if this is a partner load (has selectedCarrier or carrierOption === 'carrier')
      // Partner loads should use 'Carrier Pending', marketplace loads should use 'Open'
      const isPartnerLoad = poData.selectedCarrier || poData.carrierOption === 'carrier';
      const shippingScheduleStatus = isPartnerLoad ? SHIPPING_SCHEDULE_STATUS.CARRIER_PENDING : SHIPPING_SCHEDULE_STATUS.OPEN;
      
      await updateDoc(doc(getFailoverAwareDatabase(), 'purchaseOrders', poDoc.id), {
          status: PO_STATUS.ACTIVE,
          shippingScheduleStatus: shippingScheduleStatus, // Partner loads: 'Carrier Pending', Marketplace loads: 'Open'
        pendingCarrier: null,
        carrierId: null, // FIX: Remove carrierId from child PO
        approvedCarrier: null, // FIX: Remove approvedCarrier from child PO
        updatedAt: serverTimestamp(),
      });

    }

    // 3. Update the existing make_offer notification status to 'rejected' (original system approach)
    const existingOfferQuery = query(
      collection(getFailoverAwareDatabase(), 'notifications'),
      where('poNumber', '==', poNumber),
      where('carrierId', '==', carrierId),  // Fixed: use carrierId to match how carrier notifications query
      where('type', '==', NOTIFICATION_TYPES.MAKE_OFFER),
      where('status', '==', NOTIFICATION_STATUS.PENDING)
    );
    const existingOfferSnap = await getDocs(existingOfferQuery);
    
    if (!existingOfferSnap.empty) {
      // Update the existing make_offer notification to rejected status
      for (const offerDoc of existingOfferSnap.docs) {
        await updateDoc(offerDoc.ref, {
          status: NOTIFICATION_STATUS.REJECTED,
          updatedAt: serverTimestamp(),
          message: `Your offer for PO ${poNumber} was rejected by the shipper.`
        });
      }

      // Get load details from the existing notification for the new rejection notification
      const existingData = existingOfferSnap.docs[0].data();
      if (existingData.loadDetails) {
        // Update the loadDetails in the rejection notification with actual data
        const carrierNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
        await addDoc(carrierNotificationRef, {
          shipperId: shipperId,
          recipientId: carrierId,  // Carrier gets the rejection notification
          carrierId: carrierId,    // Add carrierId for consistency
          senderId: shipperId,     // Add senderId (shipper who rejected)
          senderName: 'Shipper',   // Add senderName for display
          poNumber: poNumber,
          status: NOTIFICATION_STATUS.REJECTED,
          type: 'offer_rejected',  // Note: 'offer_rejected' is not in constants, keeping as-is for now
          message: `Your offer for PO ${poNumber} was rejected by the shipper.`,
          loadDetails: existingData.loadDetails,  // Use actual load details from existing notification
          read: false,
          createdAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
          updatedAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
          requiresAction: false
        });

      }
    } else {

      // Fallback: Create rejection notification without load details
      const carrierNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
      await addDoc(carrierNotificationRef, {
        shipperId: shipperId,
        recipientId: carrierId,  // Carrier gets the rejection notification
        carrierId: carrierId,    // Add carrierId for consistency
        senderId: shipperId,     // Add senderId (shipper who rejected)
        senderName: 'Shipper',   // Add senderName for display
        poNumber: poNumber,
        status: NOTIFICATION_STATUS.REJECTED,
        type: 'offer_rejected',  // Fixed: use 'offer_rejected' so carrier can see it
        message: `Your offer for PO ${poNumber} was rejected by the shipper.`,
        loadDetails: {
          poNumber: poNumber,
          isMarketplace: true,  // This is a marketplace rejection
          pickupLocation: {},   // Fallback empty data
          deliveryLocation: {}, // Fallback empty data
          rate: 0,              // Fallback empty data
        },
        read: false,
        createdAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
        updatedAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
        requiresAction: false
      });

    }

    // 4. Create notification for shipper about the rejection action (ONLY for partner loads, not marketplace)
    // Marketplace loads don't need shipper confirmation - they just go back to marketplace
    // Partner loads will get their own rejection flow through different handlers

    // 5. Notification creation is now handled in step 3 with proper load details

  } catch (error) {
    Logger.error('[handleShipperRejection] Error:', error as Error);
    throw new Error(`Failed to handle shipper rejection: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Broker rejection function - handles broker rejection of carrier offers on marketplace loads
export const handleBrokerRejection = async (
  poNumber: string,
  carrierId: string,
  brokerId: string
): Promise<void> => {
  try {

    // 1. Update load status to 'Open' and remove carrierId so it goes back to marketplace
    const loadsQuery = query(
      collection(getFailoverAwareDatabase(), 'loads'), 
      where('poNumber', '==', poNumber)
    );
    const loadsSnap = await getDocs(loadsQuery);

    for (const loadDoc of loadsSnap.docs) {
      await updateDoc(doc(getFailoverAwareDatabase(), 'loads', loadDoc.id), {
        status: LOAD_STATUS.PENDING, // Keep as pending for new carrier selection
        carrierId: null, // Remove rejected carrier
        isMarketplace: false, // Keep as partner load, NOT marketplace
        updatedAt: serverTimestamp(),
        rejectionReason: 'Carrier rejected by broker',
        rejectedAt: serverTimestamp()
      });
    }

    // 2. Update broker purchase order status back to Active
    const poQuery = query(
      collection(getFailoverAwareDatabase(), 'brokerPurchaseOrders'), 
      where('poNumber', '==', poNumber)
    );
    const poSnap = await getDocs(poQuery);
    
    if (!poSnap.empty) {
      await updateDoc(doc(getFailoverAwareDatabase(), 'brokerPurchaseOrders', poSnap.docs[0].id), {
        status: PO_STATUS.ACTIVE,
        selectedCarrier: null,
        updatedAt: serverTimestamp(),
      });

    }

    // 3. Update the existing make_offer notification status to 'rejected'
    const existingOfferQuery = query(
      collection(getFailoverAwareDatabase(), 'notifications'),
      where('poNumber', '==', poNumber),
      where('carrierId', '==', carrierId),
      where('type', '==', NOTIFICATION_TYPES.MAKE_OFFER),
      where('status', '==', NOTIFICATION_STATUS.PENDING)
    );
    const existingOfferSnap = await getDocs(existingOfferQuery);
    
    if (!existingOfferSnap.empty) {
      // Update the existing make_offer notification to rejected status
      for (const offerDoc of existingOfferSnap.docs) {
        await updateDoc(offerDoc.ref, {
          status: NOTIFICATION_STATUS.REJECTED,
          updatedAt: serverTimestamp(),
          message: `Your offer for PO ${poNumber} was rejected by the broker.`
        });
      }

      // Get load details from the existing notification for the new rejection notification
      const existingData = existingOfferSnap.docs[0].data();
      if (existingData.loadDetails) {
        // Create rejection notification for carrier with actual load details
        const carrierNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
        await addDoc(carrierNotificationRef, {
          brokerId: brokerId,
          recipientId: carrierId,  // Carrier gets the rejection notification
          carrierId: carrierId,    // Add carrierId for consistency
          senderId: brokerId,      // Add senderId (broker who rejected)
          senderName: 'Broker',    // Add senderName for display
          poNumber: poNumber,
          status: NOTIFICATION_STATUS.REJECTED,
          type: 'offer_rejected',  // Use 'offer_rejected' so carrier can see it
          message: `Your offer for PO ${poNumber} was rejected by the broker.`,
          loadDetails: existingData.loadDetails,  // Use actual load details from existing notification
          read: false,
          createdAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
          updatedAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
          requiresAction: false
        });

      }
    } else {

      // Fallback: Create rejection notification without load details
      const carrierNotificationRef = collection(getFailoverAwareDatabase(), 'notifications');
      await addDoc(carrierNotificationRef, {
        brokerId: brokerId,
        recipientId: carrierId,  // Carrier gets the rejection notification
        carrierId: carrierId,    // Add carrierId for consistency
        senderId: brokerId,      // Add senderId (broker who rejected)
        senderName: 'Broker',    // Add senderName for display
        poNumber: poNumber,
        status: NOTIFICATION_STATUS.REJECTED,
        type: 'offer_rejected',  // Use 'offer_rejected' so carrier can see it
        message: `Your offer for PO ${poNumber} was rejected by the broker.`,
        loadDetails: {
          poNumber: poNumber,
          isMarketplace: true,  // This is a marketplace rejection
          pickupLocation: {},   // Fallback empty data
          deliveryLocation: {}, // Fallback empty data
          rate: 0,              // Fallback empty data
        },
        read: false,
        createdAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
        updatedAt: new Date(), // Use regular Date instead of serverTimestamp for immediate use
        requiresAction: false
      });

    }

  } catch (error) {
    Logger.error('[handleBrokerRejection] Error:', error as Error);
    throw new Error(`Failed to handle broker rejection: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}; 